import type { LabelledCount } from '../api/types';
import { count } from '../utils/format';

/**
 * One stacked bar plus a directly-labelled legend, for "these parts add up to
 * the whole". Every segment is labelled with its own value, so nothing depends
 * on reading a colour against a key.
 */
export function CompositionBar({
  buckets,
  order,
  colors,
  emptyLabel,
}: {
  buckets: LabelledCount[];
  order: string[];
  colors: Record<string, string>;
  emptyLabel: string;
}) {
  const byLabel = new Map(buckets.map((bucket) => [bucket.label, Number(bucket.products)]));
  const total = buckets.reduce((sum, bucket) => sum + Number(bucket.products), 0);

  if (total === 0) {
    return <div className="state">{emptyLabel}</div>;
  }

  const present = order.filter((label) => (byLabel.get(label) ?? 0) > 0);

  return (
    <div>
      <div className="compbar" role="img" aria-label="Composition">
        {present.map((label) => (
          <div
            key={label}
            className="compbar__seg"
            style={{ background: colors[label] ?? 'var(--surface-3)', flexGrow: byLabel.get(label) }}
            title={`${label}: ${count(byLabel.get(label))}`}
          />
        ))}
      </div>

      <div className="compbar-legend">
        {order.map((label) => (
          <div className="compbar-legend__item" key={label}>
            <span
              className="compbar-legend__rule"
              style={{ background: colors[label] ?? 'var(--surface-3)' }}
            />
            <span>
              <span className="compbar-legend__value">{count(byLabel.get(label) ?? 0)}</span>
              <div className="compbar-legend__label">
                {label} · {(((byLabel.get(label) ?? 0) / total) * 100).toFixed(0)}%
              </div>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
